// KML reader for the bulk-file scan, counterpart of geoFormats.toKml.
// togeojson parses each coordinate tuple. The document walk stays here: running
// it on the whole file turns a GroundOverlay or NetworkLink box into a polygon
// and a gx:Track into a line, and it emits LinearRings in document order, so an
// inner ring written first would become the field.
import { kml } from "@tmcw/togeojson";

type LonLat = [number, number];

function localNames(text: string): string {
  return text
    .replace(/\sxmlns:\w+="[^"]*"/g, "")
    .replace(/<(\/?)[\w.-]+:/g, "<$1");
}

function xmlEscape(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function coerce(raw: string): string | number {
  const t = raw.trim();
  if (t === "") return raw;
  const n = Number(t);
  return Number.isFinite(n) ? n : raw;
}

function childrenByLocal(el: Element, name: string): Element[] {
  return Array.from(el.children).filter((c) => c.localName === name);
}

function firstChildText(el: Element, name: string): string | null {
  const child = childrenByLocal(el, name)[0];
  return child ? (child.textContent ?? "") : null;
}

// togeojson parses with parseFloat, so "40abc" becomes 40. Number() rejects
// that token, and the ring must not gain the vertex.
function finiteTuple(token: string): boolean {
  const parts = token.split(",");
  if (parts.length < 2) return false;
  return parts.every((part) => part !== "" && Number.isFinite(Number(part)));
}

// One placemark per tuple so a junk token is omitted without shifting the rest.
function positions(text: string): LonLat[] {
  const tokens = text.trim().split(/\s+/).filter(finiteTuple);
  if (!tokens.length) return [];
  const marks = tokens
    .map(
      (token) =>
        `<Placemark><Point><coordinates>${xmlEscape(token)}</coordinates></Point></Placemark>`,
    )
    .join("");
  const doc = new DOMParser().parseFromString(
    `<kml><Document>${marks}</Document></kml>`,
    "text/xml",
  );
  const out: LonLat[] = [];
  for (const feature of kml(doc, { skipNullGeometry: true }).features) {
    const geometry = feature.geometry;
    if (!geometry || geometry.type !== "Point") continue;
    const lon = geometry.coordinates[0];
    const lat = geometry.coordinates[1];
    if (!Number.isFinite(lon) || !Number.isFinite(lat)) continue;
    out.push([lon, lat]);
  }
  return out;
}

function ringOf(boundary: Element): LonLat[] | null {
  const coords = boundary.getElementsByTagName("coordinates")[0];
  if (!coords) return null;
  const ring = positions(coords.textContent ?? "");
  return ring.length >= 4 ? ring : null;
}

function geomsOf(el: Element): GeoJSON.Geometry[] {
  const out: GeoJSON.Geometry[] = [];
  for (const child of Array.from(el.children)) {
    const kind = child.localName;
    if (kind === "Point") {
      const c = positions(firstChildText(child, "coordinates") ?? "");
      if (c.length >= 1) out.push({ type: "Point", coordinates: c[0]! });
    } else if (kind === "LineString") {
      const c = positions(firstChildText(child, "coordinates") ?? "");
      if (c.length >= 2) out.push({ type: "LineString", coordinates: c });
      else if (c.length > 0) {
        console.warn(
          "kmlRead: dropped LineString with fewer than 2 valid positions",
        );
      }
    } else if (kind === "Polygon") {
      const outer = childrenByLocal(child, "outerBoundaryIs")
        .map(ringOf)
        .find((ring) => ring != null);
      if (!outer) {
        console.warn("kmlRead: dropped Polygon without a valid outer ring");
        continue;
      }
      const holes = childrenByLocal(child, "innerBoundaryIs")
        .map(ringOf)
        .filter((ring): ring is LonLat[] => ring != null);
      out.push({ type: "Polygon", coordinates: [outer, ...holes] });
    } else if (kind === "MultiGeometry") {
      out.push(...geomsOf(child));
    }
  }
  return out;
}

function propertiesOf(pm: Element): Record<string, string | number> {
  const props: Record<string, string | number> = {};
  const name = firstChildText(pm, "name");
  if (name != null) props.name = name.trim();
  for (const ed of childrenByLocal(pm, "ExtendedData")) {
    for (const data of ed.getElementsByTagName("Data")) {
      const key = data.getAttribute("name");
      const val = data.getElementsByTagName("value")[0]?.textContent;
      if (key && val != null) props[key] = coerce(val);
    }
    for (const sd of ed.getElementsByTagName("SimpleData")) {
      const key = sd.getAttribute("name");
      if (key) props[key] = coerce(sd.textContent ?? "");
    }
  }
  return props;
}

export function readKml(text: string): GeoJSON.Feature[] {
  const doc = new DOMParser().parseFromString(localNames(text), "text/xml");
  if (doc.getElementsByTagName("parsererror").length > 0) {
    console.warn("kmlRead: XML parse error — file skipped");
    return [];
  }
  const features: GeoJSON.Feature[] = [];
  for (const pm of doc.getElementsByTagName("Placemark")) {
    const props = propertiesOf(pm);
    for (const geometry of geomsOf(pm)) {
      features.push({ type: "Feature", properties: { ...props }, geometry });
    }
  }
  return features;
}
